/**
 * XML Utilities for FracFocus Generator
 * Based on the VBA module XML_Generator
 */

// Import the getVendors function from IngredientsService
import { getVendors } from '../services/IngredientsService';

const xmlEscape = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// Cache for vendor information
let vendorsCache = null;

/**
 * Get vendor information by ID
 * @param {number} vendorId - The vendor ID
 * @returns {Promise<Object|null>} The vendor object or null if not found
 */
const getVendorById = async (vendorId) => {
  try {
    // Return null if no vendor ID provided
    if (!vendorId) {
      return null;
    }
    
    // Load vendors if not cached
    if (!vendorsCache) {
      vendorsCache = await getVendors();
    }
    
    // Find vendor by ID
    return vendorsCache.find(vendor => vendor.id === vendorId) || null;
  } catch (error) {
    console.error('Error getting vendor by ID:', error);
    return null;
  }
};


/**
 * Validate disclosure data before generating XML
 * @param {Object} wellData - Well data
 * @param {Array} chemicalData - Chemical data
 * @returns {Object} Validation result
 */
export const validateDisclosureData = (wellData, chemicalData) => {
  // Check if well data is complete
  if (!wellData) {
    return { success: false, error: 'No well data provided' };
  }

  // Check required well data fields
  const requiredWellFields = [
    'wellName', 'apiNumber', 'startDate', 'endDate', 'state', 'county', 
    'tvd', 'totalClean', 'longitude', 'latitude'
  ];
  
  for (const field of requiredWellFields) {
    if (!wellData[field] && wellData[field] !== 0) {
      return { 
        success: false, 
        error: `Missing required well data: ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}` 
      };
    }
  }

  // Check API number format
  const apiNumber = wellData.apiNumber.replace(/-/g, '');
  if (apiNumber.length !== 14) {
    return { 
      success: false, 
      error: 'API number must be 14 digits (XX-XXX-XXXXX-0000 format)' 
    };
  }

  // Check if chemical data exists
  if (!chemicalData || chemicalData.length === 0) {
    return { success: false, error: 'No chemical data provided' };
  }

  // Check for first trade name
  const firstChemical = chemicalData[0];
  if (!firstChemical.tradeName) {
    return { success: false, error: 'First chemical must have a trade name' };
  }

  // Check for supplier and purpose if trade name exists
  for (const chemical of chemicalData) {
    if (chemical.tradeName && !chemical.supplier) {
      return { 
        success: false, 
        error: `Missing supplier for trade name "${chemical.tradeName}"` 
      };
    }
    
    if (chemical.tradeName && !chemical.purpose) {
      return { 
        success: false, 
        error: `Missing purpose for trade name "${chemical.tradeName}"` 
      };
    }
    
    // Check if proprietary chemicals have a vendor assigned
    if (chemical.casNumber === 'Proprietary' && !chemical.vendorId) {
      return {
        success: false,
        error: `Missing vendor for proprietary chemical "${chemical.ingredientName || chemical.tradeName}". Please assign a vendor in the Ingredients Management section.`
      };
    }
  }

  // Check CAS number format
  for (const chemical of chemicalData) {
    if (chemical.casNumber && 
        chemical.casNumber !== 'Proprietary' && 
        chemical.casNumber !== 'CAS Not Assigned' && 
        chemical.casNumber !== 'N/A') {
      
      // Check if CAS number has two dashes
      if ((chemical.casNumber.match(/-/g) || []).length !== 2) {
        if (chemical.casNumber === 'Trade Secret' || chemical.casNumber === 'Trade secret') {
          return { 
            success: false, 
            error: 'CAS numbers must read "Proprietary" if listed as Trade Secrets' 
          };
        } else {
          return { 
            success: false, 
            error: `The CAS number ${chemical.casNumber} is in an incorrect format. All CAS numbers should include 2 hyphens.` 
          };
        }
      }
      
      // Validate CAS number checksum
      if (!validateCasNumber(chemical.casNumber)) {
        return { 
          success: false, 
          error: `The CAS number ${chemical.casNumber} is incorrect` 
        };
      }
    }
  }

  // Note: Ampersands and other special XML characters are now allowed
  // They will be properly escaped during XML generation

  // Check percent sum
  const percentSum = chemicalData.reduce((sum, chemical) => sum + (chemical.percentInHFJob || 0), 0);
  if (percentSum > 1.03) {
    return { 
      success: false, 
      error: `The Max Ingredient Concentration sum is greater than 103% (${(percentSum * 100).toFixed(2)}%)` 
    };
  } else if (percentSum < 0.97) {
    return { 
      success: false, 
      error: `The Max Ingredient Concentration sum is less than 97% (${(percentSum * 100).toFixed(2)}%)` 
    };
  }

  return { success: true };
};

/**
 * Validate CAS number format and checksum
 * @param {string} casNumber - CAS number to validate
 * @returns {boolean} Whether the CAS number is valid
 */
export const validateCasNumber = (casNumber) => {
  // Remove dashes
  const casWithoutDashes = casNumber.replace(/-/g, '');
  
  // Calculate checksum
  let sum = 0;
  for (let i = 0; i < casWithoutDashes.length - 1; i++) {
    sum += parseInt(casWithoutDashes[casWithoutDashes.length - 2 - i]) * (i + 1);
  }
  
  // Check if last digit matches checksum
  return String(sum % 10) === casWithoutDashes[casWithoutDashes.length - 1];
};


/**
 * Generate XML for FracFocus submission
 * @param {Object} wellData - Well data
 * @param {Array} chemicalData - Chemical data
 * @param {Object} waterSources - Water source data
 * @returns {Promise<string>} Generated XML
 */
export const generateXml = async (wellData, chemicalData, waterSources) => {
  // Format dates
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().replace('Z', '');
  };

  // Remove dashes from API number
  const apiNumber = wellData.apiNumber.replace(/-/g, '');

  // Get operator number from wellData or use a default
  console.log(wellData)
  const operatorNumber = wellData.operatorNumber || '12345';

  // Start building XML
  let xml = '';
  xml += '<?xml version="1.0" encoding="utf-8"?>\n';
  xml += '<FracFocus4_0 generated="' + new Date().toISOString().replace(/\.\d{3}Z$/, '') + '" xmlns="FracFocus4_0.xsd">\n';
  xml += '  <FractureJob>\n';
  xml += '    <JobStartDate>' + formatDate(wellData.startDate) + '</JobStartDate>\n';
  xml += '    <JobEndDate>' + formatDate(wellData.endDate) + '</JobEndDate>\n';
  xml += '    <OperatorNumber>' + operatorNumber + '</OperatorNumber>\n';
  xml += '    <APINumber>' + apiNumber + '</APINumber>\n';
  xml += '    <WellName>' + xmlEscape(wellData.wellName) + '</WellName>\n';
  xml += '    <SurfLongitude>' + wellData.longitude + '</SurfLongitude>\n';
  xml += '    <SurfLatitude>' + wellData.latitude + '</SurfLatitude>\n';
  xml += '    <Datum>' + (wellData.datum || 'NAD83') + '</Datum>\n';
  xml += '    <FederalWell>' + (wellData.federalWell === 'Yes' ? '1' : '0') + '</FederalWell>\n';
  xml += '    <IndianWell>' + (wellData.indianWell === 'Yes' ? '1' : '0') + '</IndianWell>\n';
  xml += '    <TVD>' + wellData.tvd + '</TVD>\n';
  xml += '    <TotalBaseWaterVolume>' + Math.round(wellData.totalClean) + '</TotalBaseWaterVolume>\n';
  xml += '    <TotalBaseNonWaterVolume>0.0</TotalBaseNonWaterVolume>\n';
  xml += '    <TotalMass>' + Math.round(wellData.totalMass || 0) + '</TotalMass>\n';
  xml += '    <EntryMethod>MSDS+</EntryMethod>\n';

  // Add water sources - UPDATED SECTION
  console.log('Adding water sources to XML:', waterSources);

  if (waterSources && waterSources.type === 'single' && waterSources.source) {
    xml += '    <WaterSource>\n';
    xml += '      <WaterSourceType>' + xmlEscape(waterSources.source) + '</WaterSourceType>\n';
    xml += '      <Percent>100</Percent>\n';
    xml += '    </WaterSource>\n';
  } else if (waterSources && waterSources.type === 'multiple' && waterSources.sources && waterSources.sources.length > 0) {
    for (const source of waterSources.sources) {
      // Check for valid source data
      if (source && (source.name || source.type)) {
        const sourceName = source.name || source.type || 'Unknown';
        const sourcePercent = source.percent || source.percentage || 0;
        
        xml += '    <WaterSource>\n';
        xml += '      <WaterSourceType>' + xmlEscape(sourceName) + '</WaterSourceType>\n';
        xml += '      <Percent>' + sourcePercent + '</Percent>\n';
        xml += '    </WaterSource>\n';
      }
    }
  } else {
    // Fallback - add a default water source if none specified
    console.log('No valid water sources found, adding default');
    xml += '    <WaterSource>\n';
    xml += '      <WaterSourceType>Not specified</WaterSourceType>\n';
    xml += '      <Percent>100</Percent>\n';
    xml += '    </WaterSource>\n';
  }

  // Group chemicals by trade name
  const chemicalsByTradeName = {};
  
  for (const chemical of chemicalData) {
    if (chemical.tradeName) {
      if (!chemicalsByTradeName[chemical.tradeName]) {
        chemicalsByTradeName[chemical.tradeName] = {
          supplier: xmlEscape(chemical.supplier),
          vendorId: chemical.vendorId, // Store vendorId for later use
          purpose: chemical.purpose,
          ingredients: []
        };
      }
      
      chemicalsByTradeName[chemical.tradeName].ingredients.push(chemical);
    } else {
      // For ingredients without trade name, add to the last trade name group
      const lastTradeName = Object.keys(chemicalsByTradeName).pop();
      if (lastTradeName) {
        chemicalsByTradeName[lastTradeName].ingredients.push(chemical);
      }
    }
  }

  // Add MSDS components
  const msdsComponents = chemicalData.filter(chem => chem.comments !== 'Non-MSDS Component');
  
  if (msdsComponents.length > 0) {
    for (const tradeName of Object.keys(chemicalsByTradeName)) {
      const tradeNameGroup = chemicalsByTradeName[tradeName];
      const msdsIngredientsInGroup = tradeNameGroup.ingredients.filter(
        ing => ing.comments !== 'Non-MSDS Component' && ing.massIngredient > 0
      );
      
      if (msdsIngredientsInGroup.length > 0) {
        // Get supplier name - if vendorId exists, use the company name from vendor
        let supplierName = tradeNameGroup.supplier;
        
        // If vendorId exists, try to get the company name
        if (tradeNameGroup.vendorId) {
          const vendor = await getVendorById(tradeNameGroup.vendorId);
          if (vendor && vendor.company) {
            supplierName = vendor.company;
          }
        }
        
        xml += '    <Purpose>\n';
        xml += '      <TradeName>' + xmlEscape(tradeName) + '</TradeName>\n';
        xml += '      <Supplier>' + xmlEscape(supplierName) + '</Supplier>\n';
        xml += '      <Purpose>' + xmlEscape(tradeNameGroup.purpose) + '</Purpose>\n';
        xml += '      <TypeIngredient>\n';
        
        for (const ingredient of msdsIngredientsInGroup) {
          xml += '        <IngredientMSDS>\n';
          xml += '          <IngredientName>' + xmlEscape(ingredient.ingredientName) + '</IngredientName>\n';
          
          if (!ingredient.casNumber) {
            xml += '          <CASNumber>NA</CASNumber>\n';
          } else {
            xml += '          <CASNumber>' + ingredient.casNumber + '</CASNumber>\n';
          }
          
          xml += '          <PercentHighAdditive>' + (ingredient.percentInAdditive * 100).toFixed(1) + '</PercentHighAdditive>\n';
          xml += '          <PercentHFJob>' + (ingredient.percentInHFJob * 100).toFixed(10) + '</PercentHFJob>\n';
          
          if (ingredient.comments && ingredient.comments !== 'Non-MSDS Component') {
            xml += '          <IngredientComment>' + xmlEscape(ingredient.comments) + '</IngredientComment>\n';
          } else {
            xml += '          <IngredientComment>None</IngredientComment>\n';
          }
          
          xml += '          <MassIngredient>' + ingredient.massIngredient.toFixed(3) + '</MassIngredient>\n';
          
          if (ingredient.casNumber === 'Proprietary') {
            // If the ingredient has a vendorId, use that vendor's information
            if (ingredient.vendorId) {
              const vendor = await getVendorById(ingredient.vendorId);
              
              if (vendor) {
                xml += '          <ClaimantCompany>' + xmlEscape(vendor.company) + '</ClaimantCompany>\n';
                xml += '          <ClaimantFirstName>' + xmlEscape(vendor.first_name || 'Contact') + '</ClaimantFirstName>\n';
                xml += '          <ClaimantLastName>' + xmlEscape(vendor.last_name || 'Person') + '</ClaimantLastName>\n';
                xml += '          <ClaimantEmail>' + xmlEscape(vendor.email || 'contact@example.com') + '</ClaimantEmail>\n';
                xml += '          <ClaimantPhone>' + xmlEscape(vendor.phone || '555-123-4567') + '</ClaimantPhone>\n';
              } else {
                // If vendor not found, try to get company name from supplier if it's a vendor ID
                let companyName = tradeNameGroup.supplier;
                
                // Check if supplier might be a vendor ID (numeric)
                if (!isNaN(tradeNameGroup.supplier)) {
                  const supplierVendor = await getVendorById(parseInt(tradeNameGroup.supplier));
                  if (supplierVendor && supplierVendor.company) {
                    companyName = supplierVendor.company;
                  }
                }
                
                xml += '          <ClaimantCompany>' + xmlEscape(companyName) + '</ClaimantCompany>\n';
                xml += '          <ClaimantFirstName>Contact</ClaimantFirstName>\n';
                xml += '          <ClaimantLastName>Person</ClaimantLastName>\n';
                xml += '          <ClaimantEmail>contact@example.com</ClaimantEmail>\n';
                xml += '          <ClaimantPhone>555-123-4567</ClaimantPhone>\n';
              }
            } else {
              // If no vendorId, try to get company name from supplier if it's a vendor ID
              let companyName = tradeNameGroup.supplier;
              
              // Check if supplier might be a vendor ID (numeric)
              if (!isNaN(tradeNameGroup.supplier)) {
                const supplierVendor = await getVendorById(parseInt(tradeNameGroup.supplier));
                if (supplierVendor && supplierVendor.company) {
                  companyName = supplierVendor.company;
                }
              }
              
              xml += '          <ClaimantCompany>' + xmlEscape(companyName) + '</ClaimantCompany>\n';
              xml += '          <ClaimantFirstName>Contact</ClaimantFirstName>\n';
              xml += '          <ClaimantLastName>Person</ClaimantLastName>\n';
              xml += '          <ClaimantEmail>contact@example.com</ClaimantEmail>\n';
              xml += '          <ClaimantPhone>555-123-4567</ClaimantPhone>\n';
            }
          }
          
          xml += '        </IngredientMSDS>\n';
        }
        
        xml += '      </TypeIngredient>\n';
        xml += '    </Purpose>\n';
      }
    }
  }

  // Add Non-MSDS components
  const nonMsdsComponents = chemicalData.filter(chem => chem.comments === 'Non-MSDS Component');
  
  if (nonMsdsComponents.length > 0) {
    xml += '    <Purpose>\n';
    xml += '      <Purpose>Other Chemicals</Purpose>\n';
    xml += '      <TypeIngredient>\n';
    
    for (const ingredient of nonMsdsComponents) {
      xml += '        <IngredientOther>\n';
      xml += '          <IngredientName>' + xmlEscape(ingredient.ingredientName) + '</IngredientName>\n';
      
      if (!ingredient.casNumber) {
        xml += '          <CASNumber>NA</CASNumber>\n';
      } else {
        xml += '          <CASNumber>' + ingredient.casNumber + '</CASNumber>\n';
      }
      
      if (ingredient.percentInHFJob) {
        xml += '          <PercentHFJob>' + (ingredient.percentInHFJob * 100).toFixed(10) + '</PercentHFJob>\n';
      }
      
      xml += '        </IngredientOther>\n';
    }
    
    xml += '      </TypeIngredient>\n';
    xml += '    </Purpose>\n';
  }

  // Close XML
  xml += '  </FractureJob>\n';
  xml += '</FracFocus4_0>';

  return xml;
};

/**
 * Generate XML from disclosure data
 * @param {Object} wellData - Well data
 * @param {Array} ingredients - Chemical ingredients data
 * @returns {Promise<string>} Generated XML
 */
export const generateXmlFromDisclosure = async (wellData, ingredients) => {
  // Validate the data first
  const validation = validateDisclosureData(wellData, ingredients);
  if (!validation.success) {
    throw new Error(validation.error);
  }
  console.log('=== XmlUtils Debug Info ===');
  console.log('generateXmlFromDisclosure - wellData.waterSource:', wellData.waterSource);
  console.log('generateXmlFromDisclosure - waterSource type:', typeof wellData.waterSource);
  console.log('generateXmlFromDisclosure - waterSource JSON:', JSON.stringify(wellData.waterSource, null, 2));
  
  // Determine water source format for XML generation
  let waterSources = { type: 'single', source: null };
  
  if (wellData.waterSource) {
    if (typeof wellData.waterSource === 'string') {
      // Single water source
      waterSources = { type: 'single', source: wellData.waterSource };
    } else if (Array.isArray(wellData.waterSource)) {
      // Multiple water sources as array
      waterSources = { 
        type: 'multiple', 
        sources: wellData.waterSource.map(source => ({
          name: source.type || source.name || source.WaterSourceType,
          percent: source.percent || source.Percent
        }))
      };
    } else if (typeof wellData.waterSource === 'object') {
      // Check if it's the water source selection object format
      if (wellData.waterSource.type === 'single' && wellData.waterSource.source) {
        // Single water source from WaterSourceSelection
        waterSources = { type: 'single', source: wellData.waterSource.source };
      } else if (wellData.waterSource.type === 'multiple' && wellData.waterSource.sources) {
        // Multiple water sources from WaterSourceSelection
        waterSources = { 
          type: 'multiple', 
          sources: wellData.waterSource.sources.map(source => ({
            name: source.name,
            percent: source.percent
          }))
        };
      } else {
        // Legacy format with source1, source2, etc.
        const sources = [];
        
        if (wellData.waterSource.source1 && wellData.waterSource.percent1) {
          sources.push({ name: wellData.waterSource.source1, percent: wellData.waterSource.percent1 });
        }
        if (wellData.waterSource.source2 && wellData.waterSource.percent2) {
          sources.push({ name: wellData.waterSource.source2, percent: wellData.waterSource.percent2 });
        }
        if (wellData.waterSource.source3 && wellData.waterSource.percent3) {
          sources.push({ name: wellData.waterSource.source3, percent: wellData.waterSource.percent3 });
        }
        if (wellData.waterSource.source4 && wellData.waterSource.percent4) {
          sources.push({ name: wellData.waterSource.source4, percent: wellData.waterSource.percent4 });
        }
        
        if (sources.length > 0) {
          waterSources = { type: 'multiple', sources };
        }
      }
    }
  }
  
  console.log('generateXmlFromDisclosure - Final waterSources for XML:', waterSources);
  
  // Generate the XML
  return generateXml(wellData, ingredients, waterSources);
};

const XmlUtils = {
  validateDisclosureData,
  validateCasNumber,
  generateXml,
  generateXmlFromDisclosure
};

export default XmlUtils;
